import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import { WppTag, WppTypography } from 'buildingBlocks';
import { DATE_FORMAT, NUMBER_DATE_FORMAT } from 'utils/dateTime';
import HomePageStrategyActions from 'components/StrategyActions/HomePageStrategyActions';
import { ALGORITHM_TYPE } from 'constantsBase';
import { DAG } from 'containers/StrategyWizard/ConfigurationByStrategyType/constants';
import { getCorrectFlightDates, getFlightDatesDurationText, isDspCrossPlatform, getStrategyStatus } from 'containers/StrategiesList/utils';
import { displayDate } from 'containers/StrategyWizard/ConfigurationByStrategyType/BudgetOptimization/utils';
import StarredUserStrategy from 'containers/StarredUserStrategy';
import Sparkline from 'containers/StrategiesList/components/Sparkline';
import { StrategyStatus, strategyStatusColorIndex } from 'containers/StrategiesList/constants';
import style, { strategyTableColumns } from 'containers/StrategiesList/style';
import { EnhancedStrategy, Strategy, User, ColDef, CellStyle, ToastConfig, Flight } from 'utils/types';
import { HeaderCheckBox, RowCheckBox } from './CheckBox';
import StrategyInfo from './StrategyInfo';

const { cellFullHeight, strategyActionsCell } = style;
const { legendBadgeContainer, sparklineContainer, strategiesHeader, flightDateTextStyles, starStrategyIconStyle } = strategyTableColumns;

export const strategiesTableColumns = (
  user: User,
  rowData: Array<EnhancedStrategy>,
  handleDeleteStrategy: (strategy: Strategy) => void,
  handleDownloadReport: (data: EnhancedStrategy) => void,
  setOpenTestModal: (x: boolean) => void,
  setSelectedTestStrat: (x: EnhancedStrategy) => void,
  showToast: (config: ToastConfig) => void,
  onDspClick: (dspId: number, flights: Array<Flight>) => void,
  setExportCsvLoading?: (x: boolean) => void,
): Array<ColDef> => [
  {
    field: 'select',
    headerComponent: () => <HeaderCheckBox rowData={rowData} />,
    sortable: false,
    resizable: false,
    cellStyle: cellFullHeight as CellStyle,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => <RowCheckBox strategy={data} />,
    flex: 1,
    maxWidth: 36,
  },
  {
    field: 'strategies',
    headerComponent: () => (
      <WppTypography tag="p" type="s-strong" style={strategiesHeader}>
        Strategies
      </WppTypography>
    ),
    sortable: false,
    resizable: false,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => (
      <StrategyInfo
        strategy={data}
        onDspClick={onDspClick}
      />
    ),
    flex: 1,
    autoHeight: true,
    wrapText: true,
  },
  {
    field: 'impressions',
    headerName: 'Impressions',
    sortable: false,
    resizable: false,
    cellStyle: cellFullHeight as CellStyle,
    width: 332,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => (
      <div style={sparklineContainer}>
        {_.isEmpty(data.analytics) ? '-' : (<Sparkline analytics={data.analytics} />)}
      </div>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    resizable: false,
    autoHeight: true,
    cellStyle: cellFullHeight as CellStyle,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => {
      const status = getStrategyStatus(data);
      return (
        <WppTag
          variant={status === StrategyStatus.completed ? 'neutral' : undefined}
          categoricalColorIndex={_.get(strategyStatusColorIndex, status)}
          label={_.capitalize(status)}
        />
      );
    },
    maxWidth: 115,
  },
  {
    field: 'flightDate',
    headerName: 'Flight Date',
    sortable: false,
    resizable: false,
    autoHeight: true,
    cellStyle: cellFullHeight as CellStyle,
    width: 255,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => {
      const flight = _.head(_.values(data.flights));
      const externalType = _.get(flight, 'externalType');
      const timezone = _.get(flight, 'timezone');
      let startDate: Date | string = _.get(flight, 'startDate');
      let endDate: Date | string = _.get(flight, 'endDate');
      let flightDatesToDisplay = `${displayDate(startDate, externalType, timezone, DATE_FORMAT)} - ${displayDate(endDate, externalType, timezone, DATE_FORMAT)}`;

      startDate = displayDate(startDate, externalType, timezone, NUMBER_DATE_FORMAT);
      endDate = displayDate(endDate, externalType, timezone, NUMBER_DATE_FORMAT);

      const isCrossPlatform = isDspCrossPlatform(data.dspId);
      if (isCrossPlatform) {
        const budgetSettings = _.get(_.find(data.algorithms, ['algorithmType', ALGORITHM_TYPE.crossPlatformOptimization.id]), `config.${DAG}.budget_settings`);
        const flightDates = getCorrectFlightDates(budgetSettings);
        startDate = _.get(flightDates, 'start_date');
        endDate = _.get(flightDates, 'end_date');
        flightDatesToDisplay = `${moment(startDate).format(DATE_FORMAT)} - ${moment(endDate).format(DATE_FORMAT)}`;
      }
      const flightDateText = getFlightDatesDurationText(startDate, endDate);
      const hasFlightDate = startDate && endDate;

      return (
        <>
          {hasFlightDate
            && (
              <>
                <WppTypography type="s-body" style={legendBadgeContainer}>{flightDatesToDisplay}</WppTypography>
                <WppTypography type="xs-body" style={flightDateTextStyles}>{flightDateText}</WppTypography>
              </>
            )}
        </>
      );
    },
  },
  {
    field: 'action',
    sortable: false,
    resizable: false,
    headerName: '',
    maxWidth: 86,
    cellStyle: cellFullHeight as CellStyle,
    cellRenderer: ({ data }: { data: EnhancedStrategy }) => (
      <div style={strategyActionsCell}>
        <StarredUserStrategy
          userId={user.id}
          strategyId={data.id}
          className="star-homepage"
          iconStyle={starStrategyIconStyle}
          withTooltip
        />
        <HomePageStrategyActions
          strategy={data}
          handleDownloadReport={handleDownloadReport}
          user={user}
          onDeleteClick={() => handleDeleteStrategy(data)}
          showAnalyticsLink
          setOpenTestModal={setOpenTestModal}
          setSelectedTestStrat={setSelectedTestStrat}
          showToast={showToast}
          setExportCsvLoading={setExportCsvLoading}
        />
      </div>
    ),
  },
];
